import React from 'react';
import PropTypes from 'prop-types';
import {
  reportBoundaryError,
  isChunkLoadError,
  maybeReloadForChunkError,
} from '../../utils/reportBoundaryError';

/**
 * Shared React error boundary that always reports via reportBoundaryError
 * (front-logs + gtag + console) before showing a fallback UI.
 * Chunk/CSS-chunk load failures auto-reload once (sessionStorage-guarded).
 */
class ObservabilityErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: null, isChunkLoad: false, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMessage: error?.message ? String(error.message).slice(0, 200) : null,
      isChunkLoad: isChunkLoadError(error),
    };
  }

  componentDidCatch(error, errorInfo) {
    const { boundary = 'ObservabilityErrorBoundary', componentName = null, extra = {} } =
      this.props;
    reportBoundaryError({
      boundary,
      error,
      errorInfo,
      componentName,
      extra: {
        ...extra,
        isChunkLoadError: isChunkLoadError(error),
      },
    });

    if (maybeReloadForChunkError(error)) {
      this.setState({ reloading: true });
    }
  }

  handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.state.reloading) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', marginTop: '20vh' }}>
          <p>Refreshing to load the latest application assets…</p>
        </div>
      );
    }

    const { fallback: Fallback } = this.props;
    if (typeof Fallback === 'function') {
      return (
        <Fallback
          errorMessage={this.state.errorMessage}
          isChunkLoad={this.state.isChunkLoad}
          reload={this.handleReload}
        />
      );
    }

    return (
      <div style={{ padding: '20px', textAlign: 'center', marginTop: '20vh' }}>
        <h2>Loading Error</h2>
        <p>There was a problem loading a part of the application.</p>
        {this.state.errorMessage ? (
          <p style={{ color: '#666', fontSize: 13, maxWidth: 480, margin: '12px auto' }}>
            {this.state.isChunkLoad
              ? 'A script or stylesheet failed to load (often fixed by a hard refresh after a deploy).'
              : this.state.errorMessage}
          </p>
        ) : null}
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            padding: '10px 20px',
            cursor: 'pointer',
            backgroundColor: '#0070f3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
          }}
        >
          Reload Application
        </button>
      </div>
    );
  }
}

ObservabilityErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  boundary: PropTypes.string,
  componentName: PropTypes.string,
  extra: PropTypes.object,
  fallback: PropTypes.func,
};

export default ObservabilityErrorBoundary;
